import * as React from "react";
import { styled } from "@mui/material/styles";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { Stack, IconButton, Box, Divider } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { itemPercentage } from "../../utilis/common";


export const CartItemTooltipContainer = styled(({ className, ...props }) => {
	return <Tooltip placement="top" children={props.children} classes={{ popper: className }} {...props} />;
})(({ theme }) => ({
	[`& .${tooltipClasses.tooltip}`]: {
		color: "rgba(0, 0, 0, 0.87)",
		fontFamily: "manrope",
		fontSize: "12px",
		border: "1px solid #dadde9",
		background: "#FFFFFF",
		boxShadow: "0px 2px 13px rgba(0, 0, 0, 0.13)",
		borderRadius: "8px",
		top: "-10px",
		padding: "10px 12px",
	},
}));

export default function CardItemTooltip({ children, item }) {
	const [currentSlide, setCurrentSlide] = React.useState(0);

	// Reset to first slide when item changes
	React.useEffect(() => {
		setCurrentSlide(0);
	}, [item?.id, item?.level1Total, item?.level2Total, item?.level3Total, item?.level4Total, item?.level5Total]);

	if (item?.isTooltip) {
		const hasLevel1 = item?.level1Total !== undefined;
		const hasLevel2 = item?.level2Total !== undefined;
		const hasLevel3 = item?.level3Total !== undefined;
		const hasLevel4 = item?.level4Total !== undefined;
		const hasLevel5 = item?.level5Total !== undefined;
		
		// Also check for card titles (for first filter, we might have card title but no level total yet)
		const hasLevel1CardTitle = item?.level1CardTitle !== undefined && item?.level1CardTitle !== null && item?.level1CardTitle !== '';
		const hasLevel2CardTitle = item?.level2CardTitle !== undefined && item?.level2CardTitle !== null && item?.level2CardTitle !== '';
		const hasLevel3CardTitle = item?.level3CardTitle !== undefined && item?.level3CardTitle !== null && item?.level3CardTitle !== '';
		const hasLevel4CardTitle = item?.level4CardTitle !== undefined && item?.level4CardTitle !== null && item?.level4CardTitle !== '';
		const hasLevel5CardTitle = item?.level5CardTitle !== undefined && item?.level5CardTitle !== null && item?.level5CardTitle !== '';
		
		// Determine number of slides
		// Slide order: 0 = Main (Filtered/Original), 1 = After 1st filter, 2 = After 2nd filter, 3 = After 3rd filter, 4 = After 4th filter, 5 = After 5th filter
		// Show slide if we have either level total OR card title (for first filter case)
		let totalSlides = 1;
		if (hasLevel5 || hasLevel5CardTitle) totalSlides = 6;
		else if (hasLevel4 || hasLevel4CardTitle) totalSlides = 5;
		else if (hasLevel3 || hasLevel3CardTitle) totalSlides = 4;
		else if (hasLevel2 || hasLevel2CardTitle) totalSlides = 3;
		else if (hasLevel1 || hasLevel1CardTitle) totalSlides = 2;
		
		// If no level totals and no card titles, show single slide (no carousel)
		if (!hasLevel1 && !hasLevel2 && !hasLevel3 && !hasLevel4 && !hasLevel5 && 
			!hasLevel1CardTitle && !hasLevel2CardTitle && !hasLevel3CardTitle && !hasLevel4CardTitle && !hasLevel5CardTitle) {
			return (
				<CartItemTooltipContainer
					title={
						<Stack direction={"column"} sx={{ padding: 0.5 }} spacing={1.5}>
							<Stack item>Current total : {item?.total || item?.value || 0}</Stack>
							<Stack item>Original total : {item?.originalTotal || 0}</Stack>
							{!item?.isTotalPercentage && (
								<Stack item>
									Percentage : {itemPercentage(item?.total || item?.value, item?.originalTotal, "percentage")}
								</Stack>
							)}
						</Stack>
					}
				>
					<span>
						{children}
					</span>
				</CartItemTooltipContainer>
			);
		}

		// Helper component to render truncated text with tooltip
		const TruncatedText = ({ text, maxWidth = "150px" }) => {
			if (!text) return null;
			return (
				<Tooltip title={text} arrow placement="top">
					<span
						style={{
							display: "inline-block",
							maxWidth: maxWidth,
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
							verticalAlign: "middle",
							flexShrink: 1,
						}}
					>
						{text}
					</span>
				</Tooltip>
			);
		};

		// Render slide content based on current slide
		// Current total is always static, slider only controls level totals
		const renderSlide = (slideIndex) => {
			// Always show Current total (static)
			const currentTotalLine = (
				<Stack item sx={{ textAlign: "center" }}>Current total : {item?.total || item?.value || 0}</Stack>
			);

			// Add divider after Current total when slider is active
			const divider = totalSlides > 1 ? <Divider sx={{ my: 0.5, width: "80%", alignSelf: "center" }} /> : null;

			// Slider controls which level total to show
			switch (slideIndex) {
				case 0: // First slide: Show Original total
					return (
						<Stack direction={"column"} sx={{ padding: 0, width: "100%" }} spacing={1.5}>
							{currentTotalLine}
							{divider}
							<Stack item sx={{ textAlign: "center" }}>Original total : {item?.originalTotal || 0}</Stack>
							{!item?.isTotalPercentage && (
								<Stack item sx={{ textAlign: "center" }}>
									Percentage : {itemPercentage(item?.total || item?.value, item?.originalTotal, "percentage")}
								</Stack>
							)}
						</Stack>
					);
				case 1: // Second slide: Show level1 total
					const rawLevel1Title = (item?.level1CardTitle && item.level1CardTitle.trim() !== '') ? item.level1CardTitle : "After 1st filter";
					const level1Title = rawLevel1Title.endsWith(' total') ? rawLevel1Title.replace(' total', '') : rawLevel1Title;
					const level1Value = item?.level1Total !== undefined ? item.level1Total : (item?.level1CardTitle && item.level1CardTitle.trim() !== '' ? 'N/A' : 0);
					return (
						<Stack direction={"column"} sx={{ padding: 0, width: "100%" }} spacing={1.5}>
							{currentTotalLine}
							{divider}
							<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, flexWrap: "nowrap", whiteSpace: "nowrap" }}>
								<TruncatedText text={level1Title} maxWidth="150px" />
								<span> : </span>
								<span>{level1Value}</span>
							</Box>							
							{!item?.isTotalPercentage && (
								<Stack item sx={{ textAlign: "center" }}>
									Percentage : {itemPercentage(item?.total || item?.value, item?.level1Total || 0, "percentage")}
								</Stack>
							)}
						</Stack>
					);
				case 2: // Third slide: Show level2 total
					const rawLevel2Title = (item?.level2CardTitle && item.level2CardTitle.trim() !== '') ? item.level2CardTitle : "After 2nd filter";
					const level2Title = rawLevel2Title.endsWith(' total') ? rawLevel2Title.replace(' total', '') : rawLevel2Title;
					const level2Value = item?.level2Total !== undefined ? item.level2Total : (item?.level2CardTitle && item.level2CardTitle.trim() !== '' ? 'N/A' : 0);
					return (
						<Stack direction={"column"} sx={{ padding: 0, width: "100%" }} spacing={1.5}>							
							{currentTotalLine}
							{divider}
							<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, flexWrap: "nowrap", whiteSpace: "nowrap" }}>
								<TruncatedText text={level2Title} maxWidth="150px" />
								<span> : </span>
								<span>{level2Value}</span>
							</Box>
							{!item?.isTotalPercentage && (
								<Stack item sx={{ textAlign: "center" }}>
									Percentage : {itemPercentage(item?.total || item?.value, item?.level2Total || 0, "percentage")}
								</Stack>
							)}
						</Stack>
					);
				case 3: // Fourth slide: Show level3 total
					const rawLevel3Title = (item?.level3CardTitle && item.level3CardTitle.trim() !== '') ? item.level3CardTitle : "After 3rd filter";
					const level3Title = rawLevel3Title.endsWith(' total') ? rawLevel3Title.replace(' total', '') : rawLevel3Title;
					const level3Value = item?.level3Total !== undefined ? item.level3Total : (item?.level3CardTitle && item.level3CardTitle.trim() !== '' ? 'N/A' : 0);
					return (
						<Stack direction={"column"} sx={{ padding: 0, width: "100%" }} spacing={1.5}>
							{currentTotalLine}
							{divider}
							<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, flexWrap: "nowrap", whiteSpace: "nowrap" }}>
								<TruncatedText text={level3Title} maxWidth="150px" />
								<span> : </span>
								<span>{level3Value}</span>
							</Box>
							{!item?.isTotalPercentage && (
								<Stack item sx={{ textAlign: "center" }}>
									Percentage : {itemPercentage(item?.total || item?.value, item?.level3Total || 0, "percentage")}
								</Stack>
							)}
						</Stack>
					);
				case 4: // Fifth slide: Show level4 total
					const rawLevel4Title = (item?.level4CardTitle && item.level4CardTitle.trim() !== '') ? item.level4CardTitle : "After 4th filter";
					const level4Title = rawLevel4Title.endsWith(' total') ? rawLevel4Title.replace(' total', '') : rawLevel4Title;
					const level4Value = item?.level4Total !== undefined ? item.level4Total : (item?.level4CardTitle && item.level4CardTitle.trim() !== '' ? 'N/A' : 0);
					return (
						<Stack direction={"column"} sx={{ padding: 0, width: "100%" }} spacing={1.5}>							
							{currentTotalLine}
							{divider}
							<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, flexWrap: "nowrap", whiteSpace: "nowrap" }}>
								<TruncatedText text={level4Title} maxWidth="150px" />
								<span> : </span>
								<span>{level4Value}</span>
							</Box>
							{!item?.isTotalPercentage && (
								<Stack item sx={{ textAlign: "center" }}>
									Percentage : {itemPercentage(item?.total || item?.value, item?.level4Total || 0, "percentage")}
								</Stack>
							)}
						</Stack>
					);
				case 5: // Sixth slide: Show level5 total
					const rawLevel5Title = (item?.level5CardTitle && item.level5CardTitle.trim() !== '') ? item.level5CardTitle : "After 5th filter";
					const level5Title = rawLevel5Title.endsWith(' total') ? rawLevel5Title.replace(' total', '') : rawLevel5Title;
					const level5Value = item?.level5Total !== undefined ? item.level5Total : (item?.level5CardTitle && item.level5CardTitle.trim() !== '' ? 'N/A' : 0);
					return (
						<Stack direction={"column"} sx={{ padding: 0, width: "100%" }} spacing={1.5}>
							{currentTotalLine}
							{divider}
							<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, flexWrap: "nowrap", whiteSpace: "nowrap" }}>
								<TruncatedText text={level5Title} maxWidth="150px" />
								<span> : </span>
								<span>{level5Value}</span>
							</Box>
							{!item?.isTotalPercentage && (
								<Stack item sx={{ textAlign: "center" }}>
									Percentage : {itemPercentage(item?.total || item?.value, item?.level5Total || 0, "percentage")}
								</Stack>
							)}
						</Stack>
					);
				default:
					return null;
			}
		};

		const handleNext = (e) => {
			e.stopPropagation();
			e.preventDefault();
			setCurrentSlide((prev) => (prev + 1) % totalSlides);
		};

		const handlePrev = (e) => {
			e.stopPropagation();
			e.preventDefault();
			setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
		};

		const handleMouseDown = (e) => {
			e.stopPropagation();
			e.preventDefault();
		};

		return (
			<CartItemTooltipContainer
				title={
					<Box sx={{ minWidth: "220px", maxWidth: "220px", position: "relative", display: "flex", flexDirection: "column", alignItems: "center", py: 0.5 }}>
						<Box sx={{ width: "100%", display: "flex", justifyContent: "center", px: 0.5 }}>
							{renderSlide(currentSlide)}
						</Box>
						{totalSlides > 1 && (
							<Stack direction={"row"} justifyContent="center" alignItems="center" sx={{ mt: 1.5, mb: 0.5 }}>
								{Array.from({ length: totalSlides }).map((_, index) => (
									<Box
										key={index}
										sx={{
											width: currentSlide === index ? "24px" : "8px",
											height: "8px",
											borderRadius: currentSlide === index ? "4px" : "50%",
											backgroundColor: currentSlide === index ? "#1976d2" : "rgba(0, 0, 0, 0.26)",
											transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
											cursor: "pointer",
											margin: "0 4px",
											"&:hover": {
												backgroundColor: currentSlide === index ? "#1565c0" : "rgba(0, 0, 0, 0.4)",
												transform: "scale(1.1)",
											},
										}}
										onClick={(e) => {
											e.stopPropagation();
											e.preventDefault();
											setCurrentSlide(index);
										}}
										onMouseDown={(e) => {
											e.stopPropagation();
											e.preventDefault();
										}}
									/>
								))}
							</Stack>
						)}
						{totalSlides > 1 && (
							<>
								<IconButton 
									size="small" 
									onClick={handlePrev}
									onMouseDown={handleMouseDown}
									sx={{ 
										position: "absolute",
										left: "-2px",
										top: "50%",
										transform: "translateY(-50%)",
										width: "24px",
										height: "24px",
										padding: 0,
										minWidth: "24px",
										borderRadius: "50%",
										backgroundColor: "rgba(25, 118, 210, 0.1)",
										border: "none",
										backdropFilter: "blur(4px)",
										boxShadow: "0 1px 4px rgba(25, 118, 210, 0.12)",
										"&:hover": {
											backgroundColor: "rgba(25, 118, 210, 0.18)",
											transform: "translateY(-50%) translateX(-2px)",
											boxShadow: "0 2px 8px rgba(25, 118, 210, 0.2)",
										},
										"&:active": {
											transform: "translateY(-50%) scale(0.9)",
											backgroundColor: "rgba(25, 118, 210, 0.22)",
										},
										transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
									}}
								>
									<ChevronLeftIcon sx={{ color: "rgba(25, 118, 210, 0.9)", fontSize: "16px" }} />
								</IconButton>
								<IconButton 
									size="small" 
									onClick={handleNext}
									onMouseDown={handleMouseDown}
									sx={{ 
										position: "absolute",
										right: "-2px",
										top: "50%",
										transform: "translateY(-50%)",
										width: "24px",
										height: "24px",
										padding: 0,
										minWidth: "24px",
										borderRadius: "50%",
										backgroundColor: "rgba(25, 118, 210, 0.1)",
										border: "none",
										backdropFilter: "blur(4px)",
										boxShadow: "0 1px 4px rgba(25, 118, 210, 0.12)",
										"&:hover": {
											backgroundColor: "rgba(25, 118, 210, 0.18)",
											transform: "translateY(-50%) translateX(2px)",
											boxShadow: "0 2px 8px rgba(25, 118, 210, 0.2)",
										},
										"&:active": {
											transform: "translateY(-50%) scale(0.9)",
											backgroundColor: "rgba(25, 118, 210, 0.22)",
										},
										transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
									}}
								>
									<ChevronRightIcon sx={{ color: "rgba(25, 118, 210, 0.9)", fontSize: "16px" }} />
								</IconButton>
							</>
						)}
					</Box>
				}
			>
				<span>
					{children}
				</span>
			</CartItemTooltipContainer>
		);
	} else {
		return children;
	}
}
